import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { CustomerDetails, TitleId } from '@common';
import {
  AddressService,
  formatFormAddressToCustomerAddress,
  FormOverrideAddress,
} from '@forms';
import { QuotePersonInfo } from '../models/quote-person-info.interface';
import {
  createQuotePersonInfo,
  formatDocumentSendMethod,
  formatEmailDetails,
  formatPreferredContactMethod,
  formatTelephoneDetails,
} from '../utils/customer.utils';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  private readonly addressService = inject(AddressService);

  // Private Signals
  private readonly _customerEmailAddress = signal<string | null>(null);
  private readonly _customerPhoneNumber = signal<string | null>(null);
  private readonly _selectedPersonInfo = signal<QuotePersonInfo | undefined>(
    undefined,
  );
  private readonly _customerSelectedToPurchasePlan = signal<boolean>(false);
  private readonly _customerSelectedToRegisterOnly = signal<boolean>(false);
  private readonly _customerMarketingPreferences = signal({
    email: false,
    post: false,
    telephone: false,
  });

  // Public Readonly Signals
  readonly customerEmailAddress = this._customerEmailAddress.asReadonly();
  readonly customerSelectedToPurchasePlan =
    this._customerSelectedToPurchasePlan.asReadonly();
  readonly customerSelectedToRegisterOnly =
    this._customerSelectedToRegisterOnly.asReadonly();
  readonly customerPhoneNumber = this._customerPhoneNumber.asReadonly();
  readonly customerAddress = computed(() => {
    const value = this.addressService.selectedAddress();
    return formatFormAddressToCustomerAddress(value);
  });
  readonly customerMarketingPreferences =
    this._customerMarketingPreferences.asReadonly();

  readonly selectedPersonInfo = this._selectedPersonInfo.asReadonly();

  readonly selectedPersonInfoAddress = computed(() => {
    return this.selectedPersonInfo()?.address;
  });

  readonly customerDetails: Signal<CustomerDetails> = computed(() => {
    return {
      address: this.customerAddress(),
      email: formatEmailDetails(this.customerEmailAddress()),
      firstName: this.selectedPersonInfo()?.firstName as string,
      initial: this.selectedPersonInfo()?.firstName.substring(0, 1) as string,
      isOverrideAddress: Boolean(
        this.selectedPersonInfo()?.address.isOverrideAddress,
      ),
      marketingPreferences: this._customerMarketingPreferences(),
      preferredContactMethod: formatPreferredContactMethod(
        this.customerEmailAddress(),
      ),
      sendMethod: formatDocumentSendMethod(this.customerEmailAddress()),
      surname: this.selectedPersonInfo()?.surname as string,
      telephoneDetails: formatTelephoneDetails(this.customerPhoneNumber()),
      title: this.selectedPersonInfo()?.title,
    };
  });

  // === Mutators ===
  setCustomerEmailAddress(email: string | null): void {
    this._customerEmailAddress.set(email);
  }

  setCustomerPhoneNumber(phoneNumber: string | null): void {
    this._customerPhoneNumber.set(phoneNumber);
  }

  setCustomerSelectedToPurchasePlan(selected: boolean): void {
    this._customerSelectedToPurchasePlan.set(selected);
  }

  setCustomerSelectedToRegisterOnly(selected: boolean): void {
    this._customerSelectedToRegisterOnly.set(selected);
  }

  setCustomerMarketingPreferences(marketingPreferences: {
    email: boolean;
    post: boolean;
    telephone: boolean;
  }): void {
    this._customerMarketingPreferences.set(marketingPreferences);
  }

  setSelectedPersonInfo(
    firstName: string,
    surname: string,
    title: TitleId,
    address: FormOverrideAddress,
  ): void {
    const quotePersonInfo: QuotePersonInfo = createQuotePersonInfo(
      firstName,
      surname,
      title,
      address,
    );
    this._selectedPersonInfo.set(quotePersonInfo);
  }
}
